import React, { useState, useEffect } from 'react';
import { Mail, Send, Inbox, ArrowUpRight, ArrowDownLeft, ChevronDown, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toLocaleTimeStringInTz, toLocaleDateStringInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface EmailLog {
  email_id_internal: string;
  from_role: string;
  email_text: string;
  email_subject_line_text: string | null;
  created_at: string;
  status: string | null;
}

interface InlineEmailThreadProps {
  leadId: string;
  leadName?: string;
  agentName?: string;
}

const formatEmailDate = (timestamp: string, tz?: string): string => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    const timeString = toLocaleTimeStringInTz(date, tz, 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    if (diffInDays === 0) return `Today at ${timeString}`;
    if (diffInDays === 1) return `Yesterday at ${timeString}`;
    if (diffInDays < 7) {
      const dayName = toLocaleDateStringInTz(date, tz, 'en-US', { weekday: 'long' });
      return `${dayName} at ${timeString}`;
    }

    const dateString = toLocaleDateStringInTz(date, tz, 'en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
    return `${dateString} at ${timeString}`;
  } catch {
    return timestamp;
  }
};

const InlineEmailThread: React.FC<InlineEmailThreadProps> = ({ leadId, leadName = 'Lead', agentName }) => {
  const tz = useTimezone();
  const [expandedSet, setExpandedSet] = useState<Set<number>>(new Set());
  const [emails, setEmails] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;

    const fetchEmails = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('email_logs')
        .select('email_id_internal, from_role, email_text, email_subject_line_text, created_at, status')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: true });

      if (!cancelled && !error && data) {
        setEmails(data);
      }
      if (!cancelled) setLoading(false);
    };

    fetchEmails();
    return () => { cancelled = true; };
  }, [leadId]);

  const handleToggle = (index: number) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 size={24} className="mx-auto text-[#1875FF] mb-3 animate-spin" />
        <p className="text-gray-400 text-sm font-medium">Loading emails...</p>
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-3">
          <Mail size={20} className="text-blue-400" />
        </div>
        <p className="text-sm text-gray-500 font-medium">No emails yet</p>
        <p className="text-xs text-gray-400 mt-1">Email conversations will appear here</p>
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gradient-to-b from-[#1875FF]/20 via-[#05E0D1]/15 to-gray-200" />

        <div className="space-y-2">
          {emails.map((email, index) => {
            const isAgent = email.from_role === 'user';
            const agentDisplayName = agentName || 'AI Agent';
            const senderName = isAgent ? agentDisplayName : leadName;
            const recipientName = isAgent ? leadName : agentDisplayName;
            const previewText = (email.email_text || '').split('\n')[0]?.substring(0, 100) || '';
            const subject = email.email_subject_line_text || '(No Subject)';
            const formattedDate = formatEmailDate(email.created_at, tz);
            const isExpanded = expandedSet.has(index);

            return (
              <div key={email.email_id_internal} className="relative flex gap-3">
                <div className="flex flex-col items-center z-10 pt-4">
                  <div className={`w-[10px] h-[10px] rounded-full ring-[3px] ring-white flex-shrink-0 ${
                    isAgent ? 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1]' : 'bg-[#05E0D1]'
                  }`} />
                </div>

                <div className="flex-1 min-w-0 pb-2">
                  <div
                    onClick={() => handleToggle(index)}
                    className={`rounded-xl border transition-all duration-200 cursor-pointer group ${
                      isExpanded
                        ? isAgent
                          ? 'border-[#1875FF]/25 shadow-md shadow-[#1875FF]/8 bg-white'
                          : 'border-[#05E0D1]/25 shadow-md shadow-[#05E0D1]/8 bg-white'
                        : 'border-gray-200/80 bg-white hover:border-[#05E0D1]/40 hover:shadow-sm'
                    }`}
                  >
                    <div className="px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isAgent ? 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10' : 'bg-[#05E0D1]/10'
                          }`}>
                            {isAgent ? <Send size={12} className="text-[#1875FF]" /> : <Inbox size={12} className="text-[#05E0D1]" />}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900 truncate">{senderName}</span>
                              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest flex-shrink-0 ${
                                isAgent ? 'bg-[#1875FF]/8 text-[#1875FF]' : 'bg-[#05E0D1]/8 text-[#05E0D1]'
                              }`}>
                                {isAgent ? <><ArrowUpRight size={9} />Sent</> : <><ArrowDownLeft size={9} />Received</>}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-400 mt-0.5">to {recipientName}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[11px] text-gray-400 font-medium hidden sm:block">{formattedDate}</span>
                          <ChevronDown size={14} className={`text-gray-300 group-hover:text-gray-500 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180 text-gray-500' : ''
                          }`} />
                        </div>
                      </div>

                      {!isExpanded && (
                        <div className="mt-2 ml-[38px]">
                          <p className="text-xs font-semibold text-gray-700 truncate">{subject}</p>
                          <p className="text-[11px] text-gray-400 truncate mt-0.5">
                            {previewText}{previewText.length >= 100 ? '...' : ''}
                          </p>
                        </div>
                      )}
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 animate-fadeIn">
                        <div className={`ml-[38px] border-t pt-3 ${isAgent ? 'border-[#1875FF]/10' : 'border-[#05E0D1]/15'}`}>
                          <div className="mb-3">
                            <span className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Subject</span>
                            <p className="text-sm font-semibold text-gray-900 mt-0.5">{subject}</p>
                          </div>
                          <div className={`rounded-lg py-3 ${isAgent ? 'bg-[#1875FF]/[0.03]' : 'bg-[#05E0D1]/[0.04]'}`}>
                            <div className="text-[13px] text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {email.email_text || '(No Content)'}
                            </div>
                          </div>
                          <div className="mt-2 sm:hidden">
                            <span className="text-[11px] text-gray-400 font-medium">{formattedDate}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-center pt-3 border-t border-gray-100 mt-3">
        <p className="text-xs text-gray-400 font-medium">
          {emails.length} email{emails.length !== 1 ? 's' : ''} in this thread
        </p>
      </div>
    </div>
  );
};

export default InlineEmailThread;
